import { z } from "zod";
import {
  optionalNonNegativeNumber,
  optionalTrimmedString,
  trimmedString,
} from "./primitives";

const leadIdSchema = trimmedString;
const emailSchema = z.preprocess(
  (value) => (typeof value === "string" ? value.trim() : value),
  z.string().email("Email must be valid.")
);

const scoreNumberSchema = z.preprocess(
  (value) => {
    if (value === null || value === undefined || value === "") {
      return value;
    }
    const parsed = typeof value === "number" ? value : Number(value);
    return Number.isNaN(parsed) ? value : parsed;
  },
  z
    .number({
      invalid_type_error: "Score must be a number.",
    })
    .min(0, "Score must be between 0 and 100.")
    .max(100, "Score must be between 0 and 100.")
);

export const leadScoringSignalInputSchema = z.object({
  industry: optionalTrimmedString,
  intentSummary: optionalTrimmedString,
  companySize: optionalNonNegativeNumber,
  urgency: z.enum(["low", "medium", "high"]).optional(),
  priorInteractions: optionalNonNegativeNumber,
  geo: optionalTrimmedString,
  notes: optionalTrimmedString,
});

export const leadScoringOverridesSchema = z.object({
  weightProfile: z.enum(["default", "speed", "enterprise"]).optional(),
  // Allow coarse overrides without opening the door to arbitrary weights yet.
  boostSignals: z.array(z.enum(["intent", "urgency", "companySize"])).optional(),
});

export const leadScoringRequestSchema = z.object({
  leadId: leadIdSchema,
  signals: leadScoringSignalInputSchema,
  overrides: leadScoringOverridesSchema.optional(),
});

export const leadScoreBandSchema = z.enum(["low", "medium", "high"]);

export const leadScoringRationaleSchema = z.object({
  key: trimmedString,
  label: trimmedString,
  description: optionalTrimmedString,
  weight: z.number().min(0).max(1),
  contribution: z.number().min(0).max(1),
});

export const leadNextActionSchema = z.object({
  key: trimmedString,
  label: trimmedString,
  description: optionalTrimmedString,
  priority: z.number().int().min(1).max(5),
});

export const leadScoringResponseSchema = z.object({
  leadId: leadIdSchema,
  score: scoreNumberSchema,
  band: leadScoreBandSchema,
  rationale: z.array(leadScoringRationaleSchema).min(1, "Rationale is required."),
  nextActions: z.array(leadNextActionSchema).min(1, "Next actions are required."),
  scoreVersion: trimmedString,
});

export const autoReplyDraftRequestSchema = z.object({
  leadId: leadIdSchema,
  band: leadScoreBandSchema,
  intentSummary: optionalTrimmedString,
  recipientName: optionalTrimmedString,
  productName: optionalTrimmedString,
  callToAction: optionalTrimmedString,
});

export const autoReplyDraftResponseSchema = z.object({
  draftId: trimmedString,
  leadId: leadIdSchema,
  subject: trimmedString,
  body: trimmedString,
  placeholders: z.array(trimmedString).default([]),
  tone: z.enum(["concise", "consultative", "executive"]).default("consultative"),
  draftVersion: trimmedString,
});

export const autoReplySendStatusSchema = z.enum(["sent", "failed"]);

export const autoReplySendRequestSchema = z.object({
  leadId: leadIdSchema,
  draftId: trimmedString,
  recipientEmail: emailSchema,
  subject: trimmedString,
  body: trimmedString,
});

export const autoReplySendResponseSchema = z.object({
  draftId: trimmedString,
  sendId: trimmedString,
  status: autoReplySendStatusSchema,
  message: optionalTrimmedString,
  sentAt: optionalTrimmedString,
});

export type LeadScoringRequest = z.infer<typeof leadScoringRequestSchema>;
export type LeadScoringResponse = z.infer<typeof leadScoringResponseSchema>;
export type LeadScoreBand = z.infer<typeof leadScoreBandSchema>;
export type LeadScoringRationale = z.infer<typeof leadScoringRationaleSchema>;
export type LeadNextAction = z.infer<typeof leadNextActionSchema>;
export type AutoReplyDraftRequest = z.infer<typeof autoReplyDraftRequestSchema>;
export type AutoReplyDraftResponse = z.infer<typeof autoReplyDraftResponseSchema>;
export type AutoReplySendRequest = z.infer<typeof autoReplySendRequestSchema>;
export type AutoReplySendResponse = z.infer<typeof autoReplySendResponseSchema>;
